/**
 * @file 재로그인 모달
 * @description 로그인이 풀리면(만료, 미사용, 폐기, 업데이트 전 토큰) 화면을 그대로 두고 띄운다
 *              - 닫기 없음 (ESC, 바깥 클릭 무시). 로그인 성공해야 사라진다
 *              - OTP 사용자는 OTP 까지 입력
 *              - 다른 계정으로 로그인 시도하면 막는다 (서버가 ACCOUNT_MISMATCH, 쿠키 안 바뀜)
 *              - 로그인되면 모달만 닫는다. 실패했던 요청(저장 등)은 다시 보내지 않고 화면도 그대로 (index.js errorLink, session.ts loginSucceeded)
 */
import { useState, useEffect, useRef, useSyncExternalStore, FormEvent } from 'react'
import { useIntl } from 'react-intl'
import { Dialog, TextField, Button, Typography, CircularProgress, InputAdornment, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import oc from 'open-color'
import { ReactComponent as GoogleLogo } from 'svg/GoogleLogo.svg'
import useStayOnTop, { STAY_ON_TOP_CLASS } from './useStayOnTop'
import { idleTimeText } from './IdleWatcher'
import {
    getRelogin,
    subscribeRelogin,
    getMe,
    gqlFetch,
    fetchMe,
    loginSucceeded,
    RELOGIN_Z_INDEX
} from 'ts-utils/session'

const GOOGLE_CLIENT_ID = `query GoogleLoginClientId { googleLoginClientId }`

const LOGIN_GOOGLE = `mutation LoginGoogle($code: String!, $expectUserNo: Int) {
    loginGoogle(code: $code, expectUserNo: $expectUserNo) { status otpTicket user_no }
}`

const REQUEST_PASSWORD_RESET = `mutation RequestPasswordReset($email: String!, $expectUserNo: Int) {
    requestPasswordReset(email: $email, expectUserNo: $expectUserNo)
}`

const LOGIN_PASSWORD = `mutation LoginPassword($email: String!, $password: String!, $expectUserNo: Int) {
    loginPassword(email: $email, password: $password, expectUserNo: $expectUserNo) { status otpTicket user_no }
}`

const LOGIN_OTP = `mutation LoginOtp($otpTicket: String!, $code: String!) {
    loginOtp(otpTicket: $otpTicket, code: $code) { status user_no }
}`

const ERROR_CODES = ['ACCOUNT_MISMATCH', 'WRONG_EMAIL', 'WRONG_PASSWORD', 'WRONG_STATUS', 'WRONG_AUTH', 'WRONG_INPUT', 'BAD_OTP', 'OTP_TICKET_EXPIRED', 'GOOGLE_FAILED']

const REASONS = ['EXPIRED', 'IDLE']

/*
** 구글 로그인: Google Identity Services 팝업(code 방식) → 서버 loginGoogle 이 code 를 확인한다
** 팝업 차단을 피하려면 버튼 클릭 안에서 바로 열어야 해서, 모달이 뜰때 스크립트와 클라이언트를 미리 준비해 둔다
*/
const GSI_SRC = 'https://accounts.google.com/gsi/client'

let gsiPromise: Promise<void> | null = null

const loadGsi = () => {
    if (!gsiPromise) {
        gsiPromise = new Promise<void>((resolve, reject) => {
            if ((window as any).google && (window as any).google.accounts && (window as any).google.accounts.oauth2) {
                resolve()
                return
            }

            const script = document.createElement('script')
            script.src = GSI_SRC
            script.async = true
            script.onload = () => resolve()
            script.onerror = () => {
                gsiPromise = null
                reject(new Error('gsi'))
            }
            document.head.appendChild(script)
        })
    }

    return gsiPromise
}

let clientIdPromise: Promise<string> | null = null

const loadClientId = () => {
    if (!clientIdPromise) {
        clientIdPromise = gqlFetch(GOOGLE_CLIENT_ID).then(data => data.googleLoginClientId || '').catch(() => {
            clientIdPromise = null
            return ''
        })
    }

    return clientIdPromise
}

const useStyles = makeStyles(theme => ({
    paper: {
        borderRadius: 16,
        padding: '36px 32px 28px',
        [theme.breakpoints.down('xs')]: {
            margin: 16,
            padding: '28px 20px 20px'
        }
    },
    icon: {
        width: 52,
        height: 52,
        margin: '0 auto 16px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: oc.indigo[0],
        color: theme.palette.primary.main
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        textAlign: 'center',
        color: oc.gray[9]
    },
    reason: {
        marginTop: 10,
        fontSize: 14,
        lineHeight: 1.6,
        textAlign: 'center',
        color: oc.gray[7],
        wordBreak: 'keep-all'
    },
    keep: {
        marginTop: 2,
        fontSize: 13,
        lineHeight: 1.6,
        textAlign: 'center',
        color: oc.gray[6],
        wordBreak: 'keep-all'
    },
    form: {
        marginTop: 24
    },
    account: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        marginBottom: 12,
        borderRadius: 8,
        background: oc.gray[0],
        border: `1px solid ${oc.gray[2]}`,
        color: oc.gray[8],
        fontSize: 15,
        overflow: 'hidden',
        '& > span': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        '& > svg': {
            flexShrink: 0,
            color: oc.gray[6]
        }
    },
    field: {
        marginBottom: 12,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8
        }
    },
    error: {
        marginBottom: 12,
        padding: '8px 12px',
        borderRadius: 8,
        background: oc.red[0],
        color: oc.red[8],
        fontSize: 13,
        lineHeight: 1.5,
        wordBreak: 'keep-all'
    },
    submit: {
        height: 48,
        borderRadius: 8,
        fontSize: 15,
        fontWeight: 600
    },
    or: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '20px 0',
        color: oc.gray[5],
        fontSize: 12,
        '&:before, &:after': {
            content: '""',
            flex: 1,
            height: 1,
            background: oc.gray[3]
        }
    },
    google: {
        height: 48,
        borderRadius: 8,
        fontSize: 15,
        color: oc.gray[8],
        background: 'white',
        border: `1px solid ${oc.gray[4]}`,
        transition: 'background .3s, border-color .3s',
        '&:hover': {
            background: oc.gray[0],
            borderColor: oc.gray[5]
        }
    },
    forgot: {
        marginTop: -4,
        marginBottom: 12,
        textAlign: 'right'
    },
    back: {
        marginTop: 16,
        textAlign: 'center'
    },
    link: {
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: 13,
        color: oc.gray[6],
        textDecoration: 'underline',
        transition: 'color .3s',
        '&:hover': {
            color: theme.palette.primary.main
        }
    },
    sent: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 16,
        padding: '12px 14px',
        borderRadius: 8,
        background: oc.indigo[0],
        color: oc.gray[8],
        fontSize: 14,
        lineHeight: 1.6,
        wordBreak: 'keep-all',
        '& > svg': {
            flexShrink: 0,
            marginTop: 1,
            color: theme.palette.primary.main
        }
    },
    otpDesc: {
        marginBottom: 12,
        fontSize: 14,
        lineHeight: 1.6,
        textAlign: 'center',
        color: oc.gray[8],
        wordBreak: 'keep-all'
    }
}))

function ReLoginModal() {
    const { formatMessage: f } = useIntl()

    const classes = useStyles()

    const relogin = useSyncExternalStore(subscribeRelogin, getRelogin)

    // 공지 팝업 등 다른 다이얼로그가 나중에 열려도 이 모달이 맨 위(포커스)
    const stayKey = useStayOnTop(relogin.open)

    const me = getMe()

    // find: 비밀번호 찾기(변경 메일 보내기), sent: 보냈음 안내
    const [ step, setStep ] = useState<'password' | 'otp' | 'find' | 'sent'>('password')
    const [ email, setEmail ] = useState('')
    const [ password, setPassword ] = useState('')
    const [ otpTicket, setOtpTicket ] = useState('')
    const [ code, setCode ] = useState('')
    const [ error, setError ] = useState('')
    const [ loading, setLoading ] = useState(false)
    const [ showPassword, setShowPassword ] = useState(false)

    // 다른 계정으로는 로그인할 수 없으니, 계정을 알면 이메일은 고정해서 보여준다
    const fixedEmail = me && me.email ? me.email : ''

    // 열릴때마다 초기화 (이메일은 지금 로그인된 계정으로 채워둔다)
    useEffect(() => {
        if (!relogin.open) return

        setStep('password')
        setEmail(fixedEmail)
        setPassword('')
        setShowPassword(false)
        setOtpTicket('')
        setCode('')
        setError('')
        setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relogin.open])

    const getErrorMessage = (e: any) => {
        const code = e && e.code
        if (!ERROR_CODES.includes(code)) return f({ id: 'component.ReLogin.error.default' })

        const extensions = (e && e.extensions) || {}

        return f({ id: `component.ReLogin.error.${code}` }, { count: extensions.count || 0, left: extensions.left || 0 })
    }

    const finish = async() => {
        const value = await fetchMe()
        loginSucceeded(value)
    }

    const moveStep = (value: 'password' | 'find') => {
        setError('')
        setLoading(false)
        setStep(value)
    }

    // 비밀번호 찾기: www 와 같은 비밀번호 변경 메일 (링크는 www 변경 화면, 새 탭). 바꾼 뒤 여기서 새 비밀번호로 로그인
    const handleFind = async(e: FormEvent) => {
        e.preventDefault()
        if (loading) return

        setError('')
        setLoading(true)

        try {
            await gqlFetch(REQUEST_PASSWORD_RESET, { email: email.trim(), expectUserNo: relogin.expectUserNo })
            setPassword('')
            setStep('sent')
        } catch (e) {
            setError(getErrorMessage(e))
        } finally {
            setLoading(false)
        }
    }

    // 비밀번호, 구글 확인 다음 (OTP 사용자면 OTP 단계로)
    const afterLogin = async(result: { status: string; otpTicket: string | null }) => {
        if (result.status === 'OTP_REQUIRED') {
            setOtpTicket(result.otpTicket || '')
            setStep('otp')
            setLoading(false)
            return
        }

        await finish()
    }

    const handlePassword = async(e: FormEvent) => {
        e.preventDefault()
        if (loading) return

        setError('')
        setLoading(true)

        try {
            const data = await gqlFetch(LOGIN_PASSWORD, { email: email.trim(), password, expectUserNo: relogin.expectUserNo })
            await afterLogin(data.loginPassword)
        } catch (e) {
            setError(getErrorMessage(e))
            setLoading(false)
        }
    }

    // 구글 코드 클라이언트 (모달이 뜰때 준비)
    const googleClient = useRef<any>(null)
    const expectUserNoRef = useRef(relogin.expectUserNo)
    expectUserNoRef.current = relogin.expectUserNo

    useEffect(() => {
        if (!relogin.open) return

        let cancel = false

        Promise.all([ loadGsi(), loadClientId() ]).then(([ , clientId ]) => {
            if (cancel || !clientId) return

            googleClient.current = (window as any).google.accounts.oauth2.initCodeClient({
                client_id: clientId,
                scope: 'openid email profile',
                ux_mode: 'popup',
                login_hint: fixedEmail || undefined,
                callback: async(response: any) => {
                    if (!response || !response.code) {
                        setLoading(false)
                        return
                    }

                    try {
                        const data = await gqlFetch(LOGIN_GOOGLE, { code: response.code, expectUserNo: expectUserNoRef.current })
                        await afterLogin(data.loginGoogle)
                    } catch (e) {
                        setError(getErrorMessage(e))
                        setLoading(false)
                    }
                },
                // 팝업을 닫았거나 못 연 경우
                error_callback: (e: any) => {
                    setLoading(false)
                    if (e && e.type === 'popup_failed_to_open') setError(f({ id: 'component.ReLogin.error.popupBlocked' }))
                }
            })
        }).catch(() => {})

        return () => {
            cancel = true
            googleClient.current = null
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relogin.open])

    const handleOtp = async(e: FormEvent) => {
        e.preventDefault()
        if (loading) return

        setError('')
        setLoading(true)

        try {
            await gqlFetch(LOGIN_OTP, { otpTicket, code: code.trim() })
            await finish()
        } catch (e: any) {
            // 5분 지났거나 5회 틀림 → 비밀번호부터 다시 (계정은 잠기지 않는다)
            if (e && e.code === 'OTP_TICKET_EXPIRED') {
                setStep('password')
                setPassword('')
                setCode('')
            }

            setError(getErrorMessage(e))
            setLoading(false)
        }
    }

    // 구글 팝업은 이 화면에서 바로 연다 (www 로 가지 않는다)
    const handleGoogle = () => {
        setError('')

        if (!googleClient.current) {
            setError(f({ id: 'component.ReLogin.error.GOOGLE_FAILED' }))
            return
        }

        setLoading(true)
        googleClient.current.requestCode()
    }

    if (!relogin.open) return null

    const reasonKey = REASONS.includes(relogin.reason) ? relogin.reason : 'default'

    // 계정 (로그인, 비밀번호 찾기 공통)
    const accountField = fixedEmail ? (
        <div className={classes.account}>
            <AccountCircleOutlinedIcon fontSize="small"/>
            <span>{fixedEmail}</span>
            {/* 비밀번호 관리자가 계정을 알아볼수 있게 */}
            <input type="hidden" name="email" autoComplete="username" value={fixedEmail}/>
        </div>
    ) : (
        <TextField
            label={f({ id: 'component.ReLogin.email' })}
            value={email}
            onChange={e => setEmail(e.target.value)}
            type="email"
            autoComplete="username"
            fullWidth
            variant="outlined"
            className={classes.field}
        />
    )

    return (
        <Dialog key={stayKey} open={true} maxWidth="xs" fullWidth disableEscapeKeyDown style={{ zIndex: RELOGIN_Z_INDEX }} className={STAY_ON_TOP_CLASS} classes={{ paper: classes.paper }}>
            <div className={classes.icon}>
                <LockOutlinedIcon/>
            </div>
            <Typography className={classes.title}>{f({ id: 'component.ReLogin.title' })}</Typography>
            <Typography className={classes.reason}>{f({ id: `component.ReLogin.reason.${reasonKey}` }, { time: idleTimeText(f) })}</Typography>
            <Typography className={classes.keep}>{f({ id: 'component.ReLogin.keep' })}</Typography>
            {
                step === 'password' ? (
                    <form onSubmit={handlePassword} className={classes.form}>
                        {accountField}
                        <TextField
                            label={f({ id: 'component.ReLogin.password' })}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            autoFocus
                            fullWidth
                            variant="outlined"
                            className={classes.field}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" tabIndex={-1} onClick={() => setShowPassword(prev => !prev)}>
                                            {showPassword ? <VisibilityIcon fontSize="small"/> : <VisibilityOffIcon fontSize="small"/>}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <div className={classes.forgot}>
                            <button type="button" className={classes.link} onClick={() => moveStep('find')}>
                                {f({ id: 'component.ReLogin.find.link' })}
                            </button>
                        </div>
                        {error && <div className={classes.error}>{error}</div>}
                        <Button type="submit" variant="contained" color="primary" disableElevation fullWidth disabled={loading || !email || !password} className={classes.submit}>
                            {loading ? <CircularProgress size={20}/> : f({ id: 'component.ReLogin.submit' })}
                        </Button>
                        <div className={classes.or}>{f({ id: 'component.ReLogin.or' })}</div>
                        <Button fullWidth onClick={handleGoogle} disabled={loading} startIcon={<GoogleLogo width="18px" height="18px"/>} className={classes.google}>
                            {f({ id: 'component.ReLogin.google' })}
                        </Button>
                    </form>
                ) : step === 'find' ? (
                    <form onSubmit={handleFind} className={classes.form}>
                        <Typography className={classes.otpDesc}>{f({ id: 'component.ReLogin.find.desc' })}</Typography>
                        {accountField}
                        {error && <div className={classes.error}>{error}</div>}
                        <Button type="submit" variant="contained" color="primary" disableElevation fullWidth disabled={loading || !email} className={classes.submit}>
                            {loading ? <CircularProgress size={20}/> : f({ id: 'component.ReLogin.find.submit' })}
                        </Button>
                        <div className={classes.back}>
                            <button type="button" className={classes.link} onClick={() => moveStep('password')}>
                                {f({ id: 'component.ReLogin.find.back' })}
                            </button>
                        </div>
                    </form>
                ) : step === 'sent' ? (
                    <div className={classes.form}>
                        <div className={classes.sent}>
                            <MailOutlineIcon/>
                            <span>{f({ id: 'component.ReLogin.find.sent' }, { email: email.trim() })}</span>
                        </div>
                        <Button variant="contained" color="primary" disableElevation fullWidth onClick={() => moveStep('password')} className={classes.submit}>
                            {f({ id: 'component.ReLogin.find.back' })}
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleOtp} className={classes.form}>
                        <Typography className={classes.otpDesc}>{f({ id: 'component.ReLogin.otp' })}</Typography>
                        <TextField
                            label="OTP"
                            value={code}
                            onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').substr(0, 6))}
                            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', style: { textAlign: 'center', letterSpacing: 6, fontSize: 18 } }}
                            autoFocus
                            fullWidth
                            variant="outlined"
                            className={classes.field}
                        />
                        {error && <div className={classes.error}>{error}</div>}
                        <Button type="submit" variant="contained" color="primary" disableElevation fullWidth disabled={loading || code.length !== 6} className={classes.submit}>
                            {loading ? <CircularProgress size={20}/> : f({ id: 'component.ReLogin.otpSubmit' })}
                        </Button>
                    </form>
                )
            }
        </Dialog>
    )
}

export default ReLoginModal
